import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { listCabCategories, listCabPackageLocations, searchCabPackages } from '../../api/cabsPublic';
import CabsBreadcrumbs from './CabsBreadcrumbs';
import { omitEmptyParams } from './helpers';

const CabsPackagesList = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [packagesData, setPackagesData] = useState({ items: [], pagination: { page: 1, totalPages: 1, total: 0 } });
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);

  const filters = useMemo(() => ({
    location: searchParams.get('location') || '',
    q: searchParams.get('q') || '',
    durationDays: searchParams.get('durationDays') || '',
    passengers: searchParams.get('passengers') || '',
    budgetMin: searchParams.get('budgetMin') || '',
    budgetMax: searchParams.get('budgetMax') || '',
    vehicleCategoryId: searchParams.get('vehicleCategoryId') || '',
    sort: searchParams.get('sort') || 'popular',
    page: Number(searchParams.get('page') || 1),
  }), [searchParams]);

  useEffect(() => {
    listCabCategories().then((res) => setCategories(res.data || res || [])).catch(() => {});
    listCabPackageLocations().then((res) => setLocations(res.data || res || [])).catch(() => {});
  }, []);

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      try {
        const res = await searchCabPackages({
          slug: filters.location || undefined,
          q: filters.q || undefined,
          durationDays: filters.durationDays || undefined,
          passengers: filters.passengers || undefined,
          budgetMin: filters.budgetMin || undefined,
          budgetMax: filters.budgetMax || undefined,
          vehicleCategoryId: filters.vehicleCategoryId || undefined,
          sort: filters.sort,
          page: filters.page,
          limit: 8,
        });
        if (!active) return;
        setPackagesData({
          items: res.data || [],
          pagination: res.pagination || { page: 1, totalPages: 1, total: (res.data || []).length },
        });
      } catch {
        if (!active) return;
        setPackagesData({ items: [], pagination: { page: 1, totalPages: 1, total: 0 } });
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [filters]);

  const updateFilters = (patch = {}, resetPage = true) => {
    const next = {
      ...filters,
      ...patch,
      page: resetPage ? 1 : patch.page || filters.page,
    };
    const params = omitEmptyParams({
      location: next.location,
      q: next.q,
      durationDays: next.durationDays,
      passengers: next.passengers,
      budgetMin: next.budgetMin,
      budgetMax: next.budgetMax,
      vehicleCategoryId: next.vehicleCategoryId,
      sort: next.sort,
      page: next.page > 1 ? next.page : '',
    });
    navigate(`/cabs/packages/list${params.toString() ? `?${params.toString()}` : ''}`);
  };

  const clearFilters = () => {
    updateFilters({
      location: '',
      q: '',
      durationDays: '',
      passengers: '',
      budgetMin: '',
      budgetMax: '',
      vehicleCategoryId: '',
      sort: 'popular',
    });
  };

  const results = packagesData.items || [];

  return (
    <section className="space-y-6">
      <CabsBreadcrumbs
        items={[
          { label: 'Home', to: '/' },
          { label: 'Cabs', to: '/cabs' },
          { label: 'Packages', to: '/cabs/packages' },
          { label: 'Listing' },
        ]}
      />

      <div className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.22em] text-[#0d6b66]">Transport Packages</p>
            <h1 className="mt-2 text-2xl font-semibold text-slate-900">Tailored road itineraries</h1>
            <p className="mt-2 text-sm text-slate-600">Browse dynamic route packages with compact cards, pricing, and variant-aware filtering.</p>
          </div>
          <button className="rounded-full border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-700" onClick={clearFilters}>
            Clear all
          </button>
        </div>

        <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-6">
          <div className="xl:col-span-2">
            <label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Location</label>
            <select
              className="mt-2 w-full rounded-2xl border border-slate-200 px-3 py-2.5 text-sm"
              value={filters.location}
              onChange={(e) => updateFilters({ location: e.target.value })}
            >
              <option value="">All locations</option>
              {locations.map((loc) => (
                <option key={loc.slug || loc.id} value={loc.slug || loc.id}>
                  {loc.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Duration</label>
            <input
              type="number"
              min="1"
              className="mt-2 w-full rounded-2xl border border-slate-200 px-3 py-2.5 text-sm"
              value={filters.durationDays}
              onChange={(e) => updateFilters({ durationDays: e.target.value })}
            />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Passengers</label>
            <input
              type="number"
              min="1"
              className="mt-2 w-full rounded-2xl border border-slate-200 px-3 py-2.5 text-sm"
              value={filters.passengers}
              onChange={(e) => updateFilters({ passengers: e.target.value })}
            />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Budget Min</label>
            <input
              type="number"
              min="0"
              className="mt-2 w-full rounded-2xl border border-slate-200 px-3 py-2.5 text-sm"
              value={filters.budgetMin}
              onChange={(e) => updateFilters({ budgetMin: e.target.value })}
            />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Budget Max</label>
            <input
              type="number"
              min="0"
              className="mt-2 w-full rounded-2xl border border-slate-200 px-3 py-2.5 text-sm"
              value={filters.budgetMax}
              onChange={(e) => updateFilters({ budgetMax: e.target.value })}
            />
          </div>
          <div className="xl:col-span-2">
            <label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Vehicle Type</label>
            <select
              className="mt-2 w-full rounded-2xl border border-slate-200 px-3 py-2.5 text-sm"
              value={filters.vehicleCategoryId}
              onChange={(e) => updateFilters({ vehicleCategoryId: e.target.value })}
            >
              <option value="">All categories</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>
          <div className="xl:col-span-2">
            <label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Search</label>
            <input
              className="mt-2 w-full rounded-2xl border border-slate-200 px-3 py-2.5 text-sm"
              value={filters.q}
              onChange={(e) => updateFilters({ q: e.target.value })}
              placeholder="Package title"
            />
          </div>
          <div className="xl:col-span-2">
            <label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Sort</label>
            <select
              className="mt-2 w-full rounded-2xl border border-slate-200 px-3 py-2.5 text-sm"
              value={filters.sort}
              onChange={(e) => updateFilters({ sort: e.target.value })}
            >
              <option value="popular">Popularity</option>
              <option value="price_low">Price low to high</option>
              <option value="price_high">Price high to low</option>
              <option value="newest">Newest</option>
            </select>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Package results</h2>
          <p className="text-sm text-slate-500">{packagesData.pagination.total || 0} matching packages</p>
        </div>
      </div>

      {loading && (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {Array.from({ length: 8 }).map((_, index) => (
            <div key={index} className="h-[360px] animate-pulse rounded-[24px] border border-slate-200 bg-white" />
          ))}
        </div>
      )}

      {!loading && !results.length && (
        <div className="rounded-[24px] border border-dashed border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
          No transport packages match these filters right now.
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {results.map((pkg) => {
          const bestVariant = pkg.bestMatchingVariant || null;
          const quickParams = new URLSearchParams();
          if (bestVariant?.id) quickParams.set('packageVariantId', bestVariant.id);
          if (filters.passengers) quickParams.set('passengers', filters.passengers);
          return (
            <article key={pkg.id} className="flex h-full flex-col overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-sm">
              <div className="h-48 bg-slate-100">
                {pkg.coverImageUrl || pkg.media?.[0]?.url ? (
                  <img src={pkg.coverImageUrl || pkg.media?.[0]?.url} alt={pkg.title} className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full items-center justify-center text-xs text-slate-400">No image</div>
                )}
              </div>
              <div className="flex flex-1 flex-col p-4">
                <p className="text-[11px] uppercase tracking-[0.18em] text-slate-400">
                  {pkg.location?.name || 'Transport package'}
                </p>
                <h3 className="mt-2 line-clamp-2 text-lg font-semibold text-slate-900">{pkg.title}</h3>
                <p className="mt-2 line-clamp-2 text-sm text-slate-600">{pkg.description || pkg.highlights || 'Custom transport itinerary.'}</p>

                <div className="mt-4 flex flex-wrap gap-2">
                  <span className="rounded-full bg-slate-100 px-3 py-1 text-[11px] text-slate-600">{pkg.days} days</span>
                  <span className="rounded-full bg-slate-100 px-3 py-1 text-[11px] text-slate-600">{pkg.nights} nights</span>
                  {bestVariant?.vehicleCategory?.name ? (
                    <span className="rounded-full bg-[#edf7f5] px-3 py-1 text-[11px] text-[#0d6b66]">
                      {bestVariant.vehicleCategory.name}
                    </span>
                  ) : null}
                </div>

                <div className="mt-auto pt-5">
                  <div className="mb-3 text-sm font-semibold text-slate-900">
                    {pkg.startingFromPrice ? `From INR ${Number(pkg.startingFromPrice).toLocaleString()}` : 'Price on request'}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Link
                      to={`/cabs/packages/${pkg.slug || pkg.id}`}
                      className="inline-flex rounded-full bg-slate-900 px-4 py-2 text-xs font-semibold text-white no-underline"
                    >
                      View details
                    </Link>
                    <Link
                      to={`/cabs/packages/${pkg.slug || pkg.id}/enquiry${quickParams.toString() ? `?${quickParams.toString()}` : ''}`}
                      className="inline-flex rounded-full border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-700 no-underline"
                    >
                      Enquire
                    </Link>
                  </div>
                </div>
              </div>
            </article>
          );
        })}
      </div>

      {!loading && packagesData.pagination.totalPages > 1 && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-[24px] border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600 shadow-sm">
          <span>
            Page {packagesData.pagination.page} of {packagesData.pagination.totalPages}
          </span>
          <div className="flex gap-2">
            <button
              className="rounded-full border border-slate-200 px-4 py-2 text-xs font-semibold"
              disabled={packagesData.pagination.page <= 1}
              onClick={() => updateFilters({ page: Math.max(1, packagesData.pagination.page - 1) }, false)}
            >
              Prev
            </button>
            <button
              className="rounded-full border border-slate-200 px-4 py-2 text-xs font-semibold"
              disabled={packagesData.pagination.page >= packagesData.pagination.totalPages}
              onClick={() => updateFilters({ page: packagesData.pagination.page + 1 }, false)}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default CabsPackagesList;
